import React from 'react'
import {
  TableContainer,
  Table,
  Paper,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  TablePagination,
  makeStyles
} from '@material-ui/core'
import { StyleSheet, StyleProp, Text, View, ViewStyle, useWindowDimensions } from 'react-native'
import { useTranslation } from 'react-i18next'
import { TableHeaderSort } from './TableHeaderSort'

export type TableDataSource = {
  [key: string]: string | null | undefined | number | JSX.Element
}

export interface SelectedRowProps {
  index: number
  selectItem: TableDataSource
}

export type SortFieldInfo = {
  field: string,
  order: 'asc' | 'desc' | undefined
}

export const OPLMuiTableStyles = makeStyles({
  root: {
    '& .MuiTableHead-root': {
      backgroundColor: '#F4F4F4'
    },
    '& .MuiTableCell-head': {
      color: '#58647A',
      fontSize: 14,
      fontStyle: 'normal',
      fontWeight: 700
    },
    '& .MuiTableCell-body': {
      color: '#1C1D1F',
      fontSize: 14,
      fontStyle: 'normal',
      fontWeight: 400
    }
  }
})

export interface CheckboxTableColum {
  title: string
  fieldName: string
  // colStyles?: React.CSSProperties
  minWidth?: number
  width?: number | string
  maxWidth?: number | string
  disabled?: boolean
  rowRender?: (props: SelectedRowProps) => JSX.Element
  headerColumnRender?: (list: any) => JSX.Element
}

type CheckboxTableProps = {
  displayedColumns: Array<CheckboxTableColum>
  dataSource: TableDataSource[]
  containerStyle?: StyleProp<ViewStyle>
  isPagination?: boolean
  totalRecord?: number
  pageSize?: number
  pageNum?: number
  setPageNum?: (value: number) => void
  setPageSize?: (value: number) => void
  setTotalRecord?: (totalNum: number) => void
  onPageChange?: (pageNum: number, pageSize: number) => void
  onRowsPerPageChange?: (pageNum: number, pageSize: number) => void
  rowsPerPageOptions?: Array<number>
  stickyHeader?: boolean
  sortItem: SortFieldInfo
  setSortItem: (item: SortFieldInfo) => void
  hasTableHeight?: boolean

}

export const CheckboxTable = (props: CheckboxTableProps) => {
  const { t } = useTranslation()
  const { height, width } = useWindowDimensions()
  const classes = OPLMuiTableStyles()
  const {
    displayedColumns,
    dataSource,
    containerStyle,
    onPageChange,
    onRowsPerPageChange,
    totalRecord = 0,
    isPagination,
    pageNum = 0,
    pageSize = 10,
    setPageNum,
    setPageSize,
    setTotalRecord,
    rowsPerPageOptions,
    sortItem,
    setSortItem,
    hasTableHeight
  } = props
  const [documentList, setDocumentList] = React.useState<TableDataSource[]>([])
  // const [sortItem, setSortItem] = React.useState<{ field: string; order: 'asc' | 'desc' | undefined }>({
  //   field: '',
  //   order: 'asc'
  // })

  const useStyles = makeStyles({
    container: {
      border: `1px solid #D3DCE6`,
      borderWidth: 1,
      borderRadius: 8,
      maxWidth: width - 40
    }
  })

  React.useEffect(() => {
    setDocumentList(dataSource)
  }, [dataSource])

  return (
    <View style={[tableStyles.container, containerStyle]}>
      <TableContainer component={Paper} className={useStyles().container} style={hasTableHeight && dataSource && dataSource.length > 0 ? { height: height - 300 } : {}}>
        <Table classes={{ root: classes.root }} stickyHeader={props.stickyHeader}>
          <TableHead>
            <TableHeaderSort
              data={documentList}
              setData={setDocumentList}
              setSortItem={setSortItem}
              sortItem={sortItem}
              column={[
                ...displayedColumns.map((columnItem) => {
                  return {
                    label: columnItem.title,
                    field: columnItem.fieldName,
                    disabled: columnItem.disabled,
                    columnRender: columnItem.headerColumnRender
                  }
                })
              ]}
            />
          </TableHead>
          <TableBody>
            {documentList &&
              documentList.map((dataItem, index) => {
                return (
                  <TableRow key={`dataItem_${index}`}>
                    {displayedColumns.map((col, subIndex) => {
                      return (
                        <TableCell key={'col' + subIndex} style={{ width: col.width, maxWidth: col.maxWidth }}>
                          {col.rowRender
                            ? col.rowRender({ index: index, selectItem: dataItem })
                            : <Text style={tableStyles.tableBodyText}>{dataItem[col.fieldName]}</Text>
                          }
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )
              })}
          </TableBody>
        </Table>
      </TableContainer>
      {isPagination && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions ?? [10, 20]}
          count={totalRecord}
          rowsPerPage={pageSize}
          page={pageNum}
          onPageChange={(e, page) => {
            setPageNum && setPageNum(page)
            onPageChange && onPageChange(page, pageSize)
          }}
          onRowsPerPageChange={(e) => {
            const pageS = Number(e.target.value)
            setPageSize && setPageSize(pageS)
            if (totalRecord <= pageNum * pageS) {
              setPageNum && setPageNum(0)
              onRowsPerPageChange && onRowsPerPageChange(0, pageS)
            } else {
              onRowsPerPageChange && onRowsPerPageChange(pageNum, pageS)
            }
          }}
          labelRowsPerPage={t('common:PaginationSize')}
          labelDisplayedRows={({ from, to, count }) => t('common:Pagination', { from, to, count })}
          component={View}
        ></TablePagination>
      )}
    </View>
  )
}
export const tableStyles = StyleSheet.create({
  container: {
    marginVertical: 24
  },
  tableBodyText: {
    flexWrap: 'wrap'
  }
})
